// D:\Casticart\frontend\src\components\common\chat\useChatDragDrop.js
import { useCallback, useEffect, useRef, useState } from 'react';

export const hasOpenModal = () => Boolean(
  typeof document !== 'undefined' && document.querySelector('.ds-modal')
);

/**
 * 共通 D&D フック（スクロール非連動＋確実リセット版）
 *
 * props:
 * - areaRef:      ドロップ許可領域（例：メッセージリストのスクロール要素）
 * - highlightRef: ハイライト枠を付ける要素（例：右側チャット全体コンテナ）※スクロールしない外枠推奨
 * - enabled:      true の時だけ受け付け
 * - onDrop(files, { rejectedCount })
 * - onInvalid(msg)
 * - maxBytes
 *
 * return:
 * - isDragActive: 表示フラグ
 * - overlayRect:  {left, top, width, height}（viewport座標, fixed用）
 */
export default function useChatDragDrop({
  areaRef,
  highlightRef,
  enabled = true,
  onDrop,
  onInvalid,
  maxBytes,
}) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [overlayRect, setOverlayRect] = useState(null);
  const depthRef = useRef(0);
  const tickingRef = useRef(false);

  const hasFiles = (dt) => {
    if (!dt) return false;
    if (dt.files && dt.files.length > 0) return true;
    const types = Array.isArray(dt.types) ? dt.types : Array.from(dt.types || []);
    return types.includes('Files') || types.includes('application/x-moz-file');
  };

  const measure = useCallback(() => {
    const el = areaRef?.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { left: r.left, top: r.top, width: r.width, height: r.height, right: r.right, bottom: r.bottom };
  }, [areaRef]);

  // ハイライト枠の付け替え（監視対象は“スクロールしない外枠”）
  useEffect(() => {
    const hl = highlightRef?.current || areaRef?.current;
    if (!hl) return;
    hl.classList.toggle('is-dragover', !!isDragActive);
    return () => hl.classList.remove('is-dragover');
  }, [highlightRef, areaRef, isDragActive]);

  useEffect(() => {
    const area = areaRef?.current;
    if (!area) return;

    const setActiveInside = (clientX, clientY) => {
      const r = measure();
      if (!r) return setIsDragActive(false);
      const inside = clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
      if (enabled && inside && !hasOpenModal()) {
        setIsDragActive(true);
        setOverlayRect({ left: r.left, top: r.top, width: r.width, height: r.height });
      } else {
        setIsDragActive(false);
        setOverlayRect(null);
      }
    };

    // ===== エリア内（drop 許可）=====
    const onAreaDragEnter = (e) => {
      if (!hasFiles(e.dataTransfer)) return;
      e.preventDefault(); e.stopPropagation();
      depthRef.current += 1;
      if (enabled && !hasOpenModal()) {
        const r = measure();
        if (r) {
          setOverlayRect({ left: r.left, top: r.top, width: r.width, height: r.height });
          setIsDragActive(true);
        }
      }
    };
    const onAreaDragOver = (e) => {
      if (!hasFiles(e.dataTransfer)) return;
      e.preventDefault(); e.stopPropagation();
      const acceptsDrop = enabled && !hasOpenModal();
      e.dataTransfer.dropEffect = acceptsDrop ? 'copy' : 'none';
      if (acceptsDrop && !tickingRef.current) {
        tickingRef.current = true;
        requestAnimationFrame(() => {
          const r = measure();
          if (r) setOverlayRect({ left: r.left, top: r.top, width: r.width, height: r.height });
          tickingRef.current = false;
        });
      }
    };
    const onAreaDragLeave = (e) => {
      const related = e.relatedTarget;
      if (related && area.contains(related)) return; // 子→子の移動は無視
      if (!hasFiles(e.dataTransfer)) return;
      e.preventDefault(); e.stopPropagation();
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) {
        setIsDragActive(false);
        setOverlayRect(null);
      }
    };
    const onAreaDrop = (e) => {
      e.preventDefault(); e.stopPropagation();
      depthRef.current = 0;
      setIsDragActive(false);

      const list = Array.from(e.dataTransfer?.files || []);
      if (!list.length) return;

      if (!enabled || hasOpenModal()) {
        onInvalid && onInvalid('ドラッグ＆ドロップは現在利用できません。');
        return;
      }

      // サイズ上限
      let rejectedCount = 0;
      const valid = !maxBytes
        ? list
        : list.filter((f) => {
            const ok = typeof f.size !== 'number' || f.size <= maxBytes;
            if (!ok) rejectedCount += 1;
            return ok;
          });

      if (!valid.length) {
        if (rejectedCount && onInvalid) onInvalid('サイズ上限を超えるファイルは送信できません。');
        return;
      }

      onDrop && onDrop(valid, { rejectedCount });
      setOverlayRect(null);
    };

    // ===== ドキュメント全体（確実リセット＋境界外判定）=====
    const onDocDragOver = (e) => {
      if (!hasFiles(e.dataTransfer)) return;
      if (hasOpenModal()) {
        hardReset();
        return;
      }
      if (typeof e.clientX === 'number' && typeof e.clientY === 'number') {
        setActiveInside(e.clientX, e.clientY);
      }
    };
    const hardReset = () => {
      depthRef.current = 0;
      setIsDragActive(false);
      setOverlayRect(null);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') hardReset(); };
    const onResizeOrScroll = () => {
      if (!isDragActive) return;
      const r = measure();
      if (r) setOverlayRect({ left: r.left, top: r.top, width: r.width, height: r.height });
    };

    area.addEventListener('dragenter', onAreaDragEnter, { capture: true });
    area.addEventListener('dragover',  onAreaDragOver,  { capture: true });
    area.addEventListener('dragleave', onAreaDragLeave, { capture: true });
    area.addEventListener('drop',      onAreaDrop,      { capture: true });

    document.addEventListener('dragover', onDocDragOver, true);
    document.addEventListener('drop',     hardReset,     true);
    window.addEventListener('dragend',    hardReset);
    window.addEventListener('blur',       hardReset);
    window.addEventListener('keydown',    onKeyDown);
    window.addEventListener('resize',     onResizeOrScroll, { passive: true });
    window.addEventListener('scroll',     onResizeOrScroll, { passive: true });

    return () => {
      area.removeEventListener('dragenter', onAreaDragEnter, { capture: true });
      area.removeEventListener('dragover',  onAreaDragOver,  { capture: true });
      area.removeEventListener('dragleave', onAreaDragLeave, { capture: true });
      area.removeEventListener('drop',      onAreaDrop,      { capture: true });

      document.removeEventListener('dragover', onDocDragOver, true);
      document.removeEventListener('drop',     hardReset,     true);
      window.removeEventListener('dragend',    hardReset);
      window.removeEventListener('blur',       hardReset);
      window.removeEventListener('keydown',    onKeyDown);
      window.removeEventListener('resize',     onResizeOrScroll);
      window.removeEventListener('scroll',     onResizeOrScroll);

      depthRef.current = 0;
      tickingRef.current = false;
    };
  }, [areaRef, highlightRef, enabled, onDrop, onInvalid, maxBytes, isDragActive, measure]);

  return { isDragActive, overlayRect };
}
